import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  ButtonBase,
  Collapse,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import ChevronRight from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MailOutline from '@mui/icons-material/MailOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import BottomSheet from './BottomSheet';
import DrilldownAboutSection from './DrilldownAboutSection';
import DrilldownSectionHeader from './DrilldownSectionHeader';
import METRIC_DEFINITIONS from './metricDefinitions';
import { ANALYTICS_HEADLINE_NUMBER_SX } from './analyticsTypography';
import MetricScopeDrilldownNote from './MetricScopeDrilldownNote';
import { fetchAllOccurrencesForEvent } from './analyticsTimeseriesFetch';
import { buildFilteredInquiryRows } from './inquiryActivityRows';
import {
  buildInquiryRootUploaderLookup,
  formatInquiryActivityLine,
} from './inquiryActivityDisplay';
import { mergeFeedRows } from './visitorSegregation';
import { buildSignupUserDirectory } from './accountCreatedEnrichment';
import { useGetAllUsersQuery } from '../../Slices/roomNateApis';
import { isValidGalleryUrl } from './FeedList';

const PAGE_SIZE = 12;

const NO_AUTO_CAPS = {
  textTransform: 'none',
  '& .MuiTypography-root': { textTransform: 'none' },
  '& span': { textTransform: 'none' },
};

const formatTimestamp = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const galleryUrlForInquiryRow = (row) => {
  const direct = row?.detail?.galleryUrl;
  if (direct && isValidGalleryUrl(direct)) return direct;
  const layoutId = row?.detail?.galleryId;
  if (layoutId && typeof window !== 'undefined') {
    return `${window.location.origin}/floor-gallery/${layoutId}`;
  }
  return null;
};

const InquiryActivityRow = ({
  row,
  idx,
  allRows,
  signupUserDirectory,
  rootByLayoutKey,
  catalogLookupLoading,
}) => {
  const line = formatInquiryActivityLine(
    row,
    allRows,
    signupUserDirectory,
    rootByLayoutKey,
    { catalogLookupLoading }
  );
  const galleryUrl = galleryUrlForInquiryRow(row);
  const borderTop = idx === 0 ? 'none' : '1px solid rgba(0,0,0,0.06)';

  const handleOpen = galleryUrl
    ? () => window.open(galleryUrl, '_blank', 'noopener,noreferrer')
    : undefined;

  return (
    <ButtonBase
      onClick={handleOpen}
      component="div"
      disabled={!galleryUrl}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        width: '100%',
        textAlign: 'left',
        px: 2,
        py: 1.25,
        borderTop,
        gap: 1.25,
        cursor: galleryUrl ? 'pointer' : 'default',
        borderRadius: '0 !important',
        textTransform: 'none !important',
        '&:hover': galleryUrl ? { bgcolor: 'rgba(0,0,0,0.02)' } : {},
      }}
    >
      <Box sx={{ flexShrink: 0, mt: '4px', display: 'flex', alignItems: 'center' }}>
        <MailOutline sx={{ fontSize: 15, color: 'rgba(0,0,0,0.38)' }} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: '0.8125rem',
            fontWeight: 500,
            color: 'rgba(0,0,0,0.82)',
            lineHeight: 1.45,
          }}
        >
          {line ?? 'Loading…'}
        </Typography>
        {row?.meta?.customer_ref &&
          !String(row.meta.customer_ref).includes('not-provided') && (
            <Typography
              sx={{
                fontSize: '0.72rem',
                color: 'rgba(0,0,0,0.5)',
                lineHeight: 1.4,
                mt: 0.25,
              }}
            >
              Listing {row.meta.customer_ref}
            </Typography>
          )}
        <Typography
          sx={{ fontSize: '0.65rem', color: 'rgba(0,0,0,0.32)', lineHeight: 1.4, mt: 0.15 }}
        >
          {formatTimestamp(row.timestamp)}
        </Typography>
      </Box>

      <Box sx={{ flexShrink: 0, mt: '3px', opacity: galleryUrl ? 1 : 0 }}>
        <OpenInNewIcon sx={{ fontSize: 13, color: 'rgba(0,0,0,0.28)' }} />
      </Box>
    </ButtonBase>
  );
};

/**
 * InquiriesActivityCard
 *
 * Hero KPI + drill-down for inquiry_submitted — same full-window /timeseries
 * walk as SharingActivityCard, with buyer-scope inquiry filtering.
 */
const InquiriesActivityCard = ({
  dateLabel = '',
  dateParams = '',
  partnerNameFilter = '',
  usersPartnerFilter = null,
  drilldownFeedRows = [],
  contextRows = [],
  catalogLayouts = [],
  catalogLookupLoading = false,
  excludeInternal = false,
  excludePartnerAgents = false,
  partnerAgentEmailDomain = null,
}) => {
  const [open, setOpen] = useState(false);
  const [listOpen, setListOpen] = useState(true);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [activityRowsRaw, setActivityRowsRaw] = useState([]);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const windowReqRef = useRef(0);

  const periodSubtitle = dateLabel
    ? `in the ${dateLabel.toLowerCase()}`
    : 'in this period';

  const mergedContextRows = useMemo(
    () => mergeFeedRows(contextRows, activityRowsRaw),
    [contextRows, activityRowsRaw]
  );

  const inquiryRows = useMemo(
    () =>
      buildFilteredInquiryRows(activityRowsRaw, {
        excludeInternal,
        excludeAgentActivity: excludePartnerAgents,
      }),
    [activityRowsRaw, excludeInternal, excludePartnerAgents]
  );

  const inquiryCount = inquiryRows.length;

  const rootByLayoutKey = useMemo(
    () => buildInquiryRootUploaderLookup(catalogLayouts),
    [catalogLayouts]
  );

  useEffect(() => {
    if (!dateParams || !partnerNameFilter) {
      setActivityRowsRaw([]);
      setMetricsLoading(false);
      return undefined;
    }

    const reqId = ++windowReqRef.current;
    setMetricsLoading(true);

    fetchAllOccurrencesForEvent('inquiry_submitted', dateParams, partnerNameFilter)
      .then((rows) => {
        if (reqId !== windowReqRef.current) return;
        setActivityRowsRaw(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (reqId !== windowReqRef.current) return;
        setActivityRowsRaw([]);
      })
      .finally(() => {
        if (reqId === windowReqRef.current) setMetricsLoading(false);
      });

    return () => {
      windowReqRef.current += 1;
    };
  }, [dateParams, partnerNameFilter]);

  useEffect(() => {
    if (!open) {
      setVisibleCount(PAGE_SIZE);
      setListOpen(true);
    }
  }, [open]);

  const allRowsForStats = useMemo(
    () => [
      ...(Array.isArray(drilldownFeedRows) ? drilldownFeedRows : []),
      ...inquiryRows,
      ...mergedContextRows,
    ],
    [drilldownFeedRows, inquiryRows, mergedContextRows]
  );

  const usersQueryArg =
    !usersPartnerFilter || usersPartnerFilter === 'all' ? null : usersPartnerFilter;
  const { data: signupUsersRaw } = useGetAllUsersQuery(usersQueryArg, { skip: !open });
  const signupUserDirectory = useMemo(
    () => buildSignupUserDirectory(signupUsersRaw),
    [signupUsersRaw]
  );

  const visibleRows = inquiryRows.slice(0, visibleCount);
  const hasMore = visibleCount < inquiryRows.length;

  if (metricsLoading) {
    return (
      <Skeleton
        variant="rounded"
        height={132}
        sx={{ borderRadius: '14px', bgcolor: 'rgba(0,0,0,0.04)' }}
      />
    );
  }

  return (
    <>
      <ButtonBase
        onClick={() => setOpen(true)}
        sx={{
          ...NO_AUTO_CAPS,
          position: 'relative',
          width: '100%',
          textAlign: 'left',
          display: 'block',
          minHeight: { xs: 132, sm: 140 },
          borderRadius: '14px !important',
          border: '1px solid rgba(0,0,0,0.06)',
          bgcolor: '#fff',
          p: { xs: 1.75, sm: 2 },
          transition: 'background 160ms ease, border-color 160ms ease, transform 120ms ease',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.015)', borderColor: 'rgba(0,0,0,0.1)' },
          '&:active': { transform: 'scale(0.997)' },
          cursor: 'pointer',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: { xs: 1, sm: 1.25 }, pr: 3 }}>
          <MailOutline sx={{ fontSize: 15, color: 'rgba(0,0,0,0.4)', flexShrink: 0 }} />
          <Typography
            sx={{
              fontSize: { xs: '0.875rem', md: '0.9375rem' },
              fontWeight: 500,
              color: 'rgba(0,0,0,0.5)',
            }}
          >
            Listing interest
          </Typography>
        </Stack>

        <Typography sx={ANALYTICS_HEADLINE_NUMBER_SX}>
          {inquiryCount.toLocaleString()}
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: '0.75rem', md: '0.8125rem' },
            fontWeight: 400,
            color: 'rgba(0,0,0,0.45)',
            mt: 0.3,
            lineHeight: 1.4,
          }}
        >
          {periodSubtitle}
        </Typography>

        <ChevronRight
          sx={{
            position: 'absolute',
            top: { xs: 15, sm: 19 },
            right: { xs: 11, sm: 15 },
            fontSize: 18,
            color: 'rgba(0,0,0,0.3)',
            opacity: 0.45,
            pointerEvents: 'none',
          }}
        />
      </ButtonBase>

      <BottomSheet
        open={open}
        onClose={() => setOpen(false)}
        title="Listing interest"
        subtitle={dateLabel || undefined}
      >
        <MetricScopeDrilldownNote
          scope="demand"
          excludeInternal={excludeInternal}
          excludePartnerAgents={excludePartnerAgents}
          partnerAgentEmailDomain={partnerAgentEmailDomain}
        />
        {METRIC_DEFINITIONS.inquiries && (
          <DrilldownAboutSection
            body={METRIC_DEFINITIONS.inquiries.body}
            sections={METRIC_DEFINITIONS.inquiries.sections}
            sheetOpen={open}
          />
        )}
        <Box sx={{ mb: 2.5 }}>
          <DrilldownSectionHeader
            title="Recent listing interest"
            countLabel={`${visibleRows.length} of ${inquiryRows.length} shown`}
            collapsible
            open={listOpen}
            onToggle={() => setListOpen((v) => !v)}
          />
          <Collapse in={listOpen}>
            <Box
              sx={{
                borderRadius: '12px',
                border: '1px solid rgba(0,0,0,0.06)',
                bgcolor: '#fff',
                overflow: 'hidden',
              }}
            >
              {visibleRows.length === 0 ? (
                <Typography
                  sx={{ px: 2, py: 2, fontSize: '0.8rem', color: 'rgba(0,0,0,0.45)' }}
                >
                  {metricsLoading ? 'Loading…' : 'No listing interest in this period'}
                </Typography>
              ) : (
                visibleRows.map((row, idx) => (
                  <InquiryActivityRow
                    key={row._id || `${row.timestamp}-${row?.detail?.inquirerEmail}-${idx}`}
                    row={row}
                    idx={idx}
                    allRows={allRowsForStats}
                    signupUserDirectory={signupUserDirectory}
                    rootByLayoutKey={rootByLayoutKey}
                    catalogLookupLoading={catalogLookupLoading}
                  />
                ))
              )}
              {hasMore && (
                <ButtonBase
                  onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}
                  sx={{
                    width: '100%',
                    py: 1.25,
                    borderTop: '1px solid rgba(0,0,0,0.06)',
                    fontSize: '0.8rem',
                    fontWeight: 500,
                    color: 'rgba(99,102,241,0.9)',
                    borderRadius: '0 0 12px 12px !important',
                    textTransform: 'none !important',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' },
                  }}
                >
                  Load more
                  <ExpandMoreIcon sx={{ fontSize: 16, ml: 0.5 }} />
                </ButtonBase>
              )}
            </Box>
          </Collapse>
        </Box>
      </BottomSheet>
    </>
  );
};

export default InquiriesActivityCard;
